import type { ApiCompetitionResult } from '@/components/results/middleware/competition-converter.models'
import type { CompEvent, StoreCompResultsState } from '../store-comp-results'
import type { T } from 'vitest/dist/reporters-P7C2ytIv.js'

export interface TeamResult {
  teamName: string
  club: string
  egId: number
  positions: (number | null)[]
  totalScore: number
  athleteCount: number
  athletes: TeamAthleteDetail[]
}

export interface ClubResultSeries {
  clubName: string
  egIds: number[]
  teams: Record<number, TeamResult>
  totalScore: number
  topAthletes: TeamAthleteDetail[]
  egIdTopAthletesMap?: Record<number, TeamAthleteDetail[]>
  dateTopAthletesMap?: Record<string, number>
  qualification: {
    isQualified: boolean
    details: string
  }
  positionTotals?: Record<number, number>
}

export interface TeamAthleteDetail {
  position: number
  athleteId: number
  athleteName: string
  club: string
  bibNo: string
  score: string
}

export interface TeamResultsData {
  teams: TeamResult[]
  eventGroups: number[]
  eventGroupDescriptions: Record<number, string>
}

/**
 * Processes competition results to calculate team scores
 * Groups results by event group (egId), then by club, takes top 4 positions per team
 * @param results Array of competition results
 * @param maxAthletesPerTeam Maximum number of athletes to count per team (default: 4)
 * @returns Processed team results data
 */
export function processTeamResults(
  results: ApiCompetitionResult[],
  maxAthletesPerTeam: number = 4
): TeamResultsData {
  // Group results by egId first
  const resultsByEgId = new Map<number, ApiCompetitionResult[]>()
  const eventGroupDescriptions: Record<number, string> = {}

  for (const result of results) {
    if (!resultsByEgId.has(result.egId)) {
      resultsByEgId.set(result.egId, [])
    }
    resultsByEgId.get(result.egId)!.push(result)

    // Extract description from rhOptions if available
    if (result.rhOptions && !eventGroupDescriptions[result.egId]) {
      try {
        const rhOptions =
          typeof result.rhOptions === 'string' ? JSON.parse(result.rhOptions) : result.rhOptions
        if (rhOptions && rhOptions.description) {
          eventGroupDescriptions[result.egId] = rhOptions.description
        }
      } catch (e) {
        // Ignore parsing errors
      }
    }
  }

  const teams: TeamResult[] = []
  const eventGroups = Array.from(resultsByEgId.keys())

  // Process each event group
  for (const [egId, egResults] of resultsByEgId) {
    // Group by club within this event group
    const resultsByClub = new Map<string, ApiCompetitionResult[]>()

    for (const result of egResults) {
      // Filter out disqualified athletes (position 0 or null)
      if (result.position && result.position > 0) {
        if (!resultsByClub.has(result.club)) {
          resultsByClub.set(result.club, [])
        }
        resultsByClub.get(result.club)!.push(result)
      }
    }

    // Process each club/team
    for (const [club, clubResults] of resultsByClub) {
      // Sort by position (ascending)
      clubResults.sort((a, b) => a.position - b.position)

      // Take top N athletes (default 4)
      //const topAthletes = clubResults.slice(0, maxAthletesPerTeam)
      const topAthletes = clubResults

      // Extract positions for display and build athlete details
      const positions: (number | null)[] = []
      const athletes: TeamAthleteDetail[] = []
      let totalScore = 0

      // put all athletes in the list
      for (const athlete of topAthletes) {
        const position = athlete.position
        positions.push(position)
        totalScore += position
        athletes.push({
          position,
          athleteId: athlete.athleteId,
          athleteName: athlete.athlete,
          club: athlete.club,
          bibNo: athlete.bibNo,
          score: athlete.score
        })
      }

      /*
      for (let i = 0; i < maxAthletesPerTeam; i++) {
        if (i < topAthletes.length) {
          const athlete = topAthletes[i]
          const position = athlete.position
          positions.push(position)
          totalScore += position

          athletes.push({
            position,
            athleteId: athlete.athleteId,
            athleteName: athlete.athlete,
            club: athlete.club,
            bibNo: athlete.bibNo,
            score: athlete.score
          })
        } else {
          positions.push(null) // No athlete in this position
        }
      }
      */

      // Add team result
      teams.push({
        teamName: `${club}`,
        club,
        egId,
        positions,
        totalScore,
        athleteCount: topAthletes.length,
        athletes
      })

      /*
      // Only include teams with at least maxAthletesPerTeam athletes
      if (topAthletes.length >= maxAthletesPerTeam) {
        teams.push({
          teamName: `${club}`,
          club,
          egId,
          positions,
          totalScore,
          athleteCount: topAthletes.length,
          athletes
        })
      }
      */
    }
  }

  return {
    teams,
    eventGroups,
    eventGroupDescriptions
  }
}

/**
 * Sorts team results by total score (ascending - lower score is better)
 * @param teams Array of team results
 * @returns Sorted array of team results
 */
export function sortTeamsByScore(teams: TeamResult[]): TeamResult[] {
  return [...teams].sort((a, b) => a.totalScore - b.totalScore)
}

/**
 * Sorts team results by team name (alphabetical)
 * @param teams Array of team results
 * @returns Sorted array of team results
 */
export function sortTeamsByName(teams: TeamResult[]): TeamResult[] {
  return [...teams].sort((a, b) => a.teamName.localeCompare(b.teamName))
}

/**
 * {"groupIds":[18201,18202],"maxInHeat":8,"seed":{"laneCount":8,"qualifyToEg":{"rules":{"auto":0,"nonAuto":0},"eventNo":0,"isMultiEvent":false}},"checkIn":{"from":-1,"to":-1,"seedOnEntries":false}}
 * create a function that takes an array of schedules.
 * For each schedule, get the first entry and get the ageGroup.
 * Return an object where the keys are the ageGroups and the values are arrays of egIds.
 * Get the groupIds from the schedule options if they exist, otherwise use the egId.
 */
export function groupSchedulesBySeriesName(schedules: CompEvent[]): Record<string, number[]> {
  const grouped: Record<string, number[]> = {}

  for (const schedule of schedules) {
    let seriesName = schedule.name
    // does it have teamResults enabled?
    if (schedule.egOptions?.teamResults?.enabled) {
      seriesName = schedule.egOptions.teamResults.seriesName
        ? schedule.egOptions.teamResults.seriesName
        : schedule.name
    }
    if (!grouped[seriesName]) {
      grouped[seriesName] = []
    }
    grouped[seriesName].push(schedule.egId)
  }

  return grouped
}

export interface TeamResultsState {
  seriesNameToEgIdsMap: Record<string, number[]>
  teams: TeamResultsData
  teamsSorted: TeamResult[]
  seriesNames: string[]
}

//  a function that builds state required for TeamResults.vue
export function buildTeamResultsState(compResultsState: StoreCompResultsState): TeamResultsState {
  const MAX_TEAM_RESULTS = 1000

  const teams: TeamResultsData = processTeamResults(
    compResultsState.apiServerResponse?.data.results || [],
    MAX_TEAM_RESULTS
  )

  const teamsSorted = sortTeamsByScore(teams.teams)

  const events = Object.values(compResultsState.competitionOnTheDay.events)

  // build map of seriesNames to egIds
  const seriesNameToEgIdsMap = groupSchedulesBySeriesName(events || [])

  const seriesNames = Object.keys(seriesNameToEgIdsMap).sort()

  // if (sortKey.value === 'score') {
  //   teams = sortTeamsByScore(teams)
  //   if (sortDirection.value === 'desc') {
  //     teams.reverse()
  //   }
  // } else if (sortKey.value === 'name') {
  //   teams = sortTeamsByName(teams)
  //   if (sortDirection.value === 'desc') {
  //     teams.reverse()
  //   }
  // }

  return {
    teams,
    teamsSorted,
    seriesNames,
    seriesNameToEgIdsMap
  }
}

// for a specific seriesName, get the egIds and filter the teams to only those egIds
export function getTeamsForSeriesName(
  teamResultsState: TeamResultsState,
  seriesName: string
): TeamResult[] {
  if (!seriesName) {
    return []
  }

  const teams = [...(teamResultsState?.teams.teams || [])]

  const egIds = teamResultsState.seriesNameToEgIdsMap[seriesName]
  return teams.filter((team) => egIds.includes(team.egId))
}

// given an array of teams and a max athletesPerTeam, filter the teams to only those with at least maxAthletesPerTeam athletes
export function filterTeamsByAthleteCount(
  teams: TeamResult[],
  maxAthletesPerTeam: number
): TeamResult[] {
  return teams.filter((team) => team.athleteCount >= maxAthletesPerTeam)
}

export interface SplitTeamsByAthleteCount {
  atLeast: TeamResult[]
  lessThan: TeamResult[]
}

// given an array of teams and a max athletesPerTeam, return an object with two arrays: one with teams that have at least maxAthletesPerTeam athletes, and one with teams that have less than maxAthletesPerTeam athletes
export function splitTeamsByAthleteCount(
  teams: TeamResult[],
  maxAthletesPerTeam: number
): SplitTeamsByAthleteCount {
  const response: SplitTeamsByAthleteCount = {
    atLeast: teams.filter((team) => team.athleteCount >= maxAthletesPerTeam),
    lessThan: teams.filter((team) => team.athleteCount < maxAthletesPerTeam)
  }

  // go through each array and sort by score
  response.atLeast = sortTeamsByScore(response.atLeast)
  response.lessThan = sortTeamsByScore(response.lessThan)

  // only take the top maxAthletesPerTeam in each team
  response.atLeast = response.atLeast.map((team) => ({
    ...team,
    athletes: team.athletes.slice(0, maxAthletesPerTeam)
  }))
  response.lessThan = response.lessThan.map((team) => ({
    ...team,
    athletes: team.athletes.slice(0, maxAthletesPerTeam)
  }))

  return response
}

/*
const seriesNameDisplayData = computed(() => {
  if (!teamResultsState.value) {
    return {
      teamsForSeriesName: {
        seriesName: '',
        teams: []
      },
      teamsByAthleteCount: {}
    }
  }

  // Build display data for series names
  const teamsForSeriesName = getTeamsForSeriesName(teamResultsState.value, seriesNameSelected.value)
  const teamsByAthleteCount = splitTeamsByAthleteCount(teamsForSeriesName, 4)

  return {
    teamsForSeriesName,
    teamsByAthleteCount
  }
})*/

export interface BuildSeriesNameDisplayDataResult {
  teamsForSeriesName: {
    seriesName: string
    teams: TeamResult[]
  }
  teamsByAthleteCount: {
    atLeast: TeamResult[]
    lessThan: TeamResult[]
  }
  maxAthletesPerTeam: number
  series: ClubResultSeries[]
  seriesNonQualified: ClubResultSeries[]
  dateBasedKeyAllTeams?: Record<string, Set<string>>
  sortedDateBasedKeyAllTeams?: Record<string, Set<string>>
  columnsToLayout: number
}

//  This function builds display data for a specific seriesName, E,g, 'XC Under 13 Boys'
export function buildSeriesNameDisplayData(
  storeCompResultsState: StoreCompResultsState,
  teamResultsState: TeamResultsState,
  seriesName: string
): BuildSeriesNameDisplayDataResult {
  const defaultResult: BuildSeriesNameDisplayDataResult = {
    teamsForSeriesName: {
      seriesName: '',
      teams: []
    },
    teamsByAthleteCount: {
      atLeast: [],
      lessThan: []
    },
    maxAthletesPerTeam: 0,
    series: [],
    seriesNonQualified: [],
    columnsToLayout: 0
  }

  if (!teamResultsState) {
    defaultResult
  }

  if (!seriesName) {
    return defaultResult
  }

  // Build display data for series names
  const teamsForSeriesName = {
    seriesName,
    teams: getTeamsForSeriesName(teamResultsState, seriesName)
  }

  //  Get schedule by seriesName to find maxAthletesPerTeam from egOptions

  const events = Object.values(storeCompResultsState.competitionOnTheDay.events)
  // find first schedule that matches an egid in this seriesName
  const egIds = teamResultsState.seriesNameToEgIdsMap[seriesName]
  const schedule = events.find((event) => egIds.includes(event.egId))

  let maxAthletesPerTeam = 3
  if (schedule) {
    maxAthletesPerTeam = schedule.egOptions?.teamResults?.teamSize || 3
  }

  const teamsByAthleteCount = splitTeamsByAthleteCount(teamsForSeriesName.teams, maxAthletesPerTeam)

  /**
 * Need to merge teams.  THey will be split by egId currently.
  const mergedTeams: TeamResult[] = []
  const teamMap: Record<string, TeamResult> = {}

  Then we need to know how manhy competiton dates have been run for this seriesName.
  For a team to be eligible, they need to have at least maxAthletesPerTeam athletes across all competition dates.
  for (const team of teamsForSeriesName.teams)
 */

  //  how many egIds are there for this seriesName?   Need to make sure egIds are unique.
  // Or is it unique dates...but that would be restrictive???  For now, unique ids
  // in splitTeamsByAthleteCount.atLeast
  const howManyEgIdsRequired = new Set<number>()
  for (const team of teamsByAthleteCount.atLeast) {
    howManyEgIdsRequired.add(team.egId)
  }

  // so we need all clubs to have least have howManyEgIdsRequired competition dates with at least
  // maxAthletesPerTeam athletes to be eligible.

  const series = mergeTeamsByClub(
    teamsByAthleteCount.atLeast,
    howManyEgIdsRequired.size,
    maxAthletesPerTeam,
    storeCompResultsState
  )

  // qualified series are those with isQualified = true and remove Unattached teams
  const qualifiedSeries = series.filter(
    (series) => series.qualification.isQualified && series.clubName !== 'Unattached'
  )

  const unqualifiedSeries = series.filter(
    (series) => !series.qualification.isQualified || series.clubName === 'Unattached'
  )

  // seriesNonQualified = mergeTeamsByClub(teamsByAthleteCount.lessThan, howManyEgIdsRequired.size, maxAthletesPerTeam)
  let seriesNonQualified = mergeTeamsByClub(
    teamsByAthleteCount.lessThan,
    howManyEgIdsRequired.size,
    maxAthletesPerTeam,
    storeCompResultsState
  )

  // merge the unqualified series into the seriesNonQualified
  seriesNonQualified = [...seriesNonQualified, ...unqualifiedSeries]

  //  This is to keep a running total of date-based keys across all teams.
  //  Used to layout columns in TeamResults.vue
  //  All teams should be using the same key.
  //  Store array of teams, should we use a Set or Map?
  const dateBasedKeyAllTeams: Record<string, Set<string>> = {}
  for (const teamSeries of [...qualifiedSeries, ...seriesNonQualified]) {
    if (teamSeries.dateTopAthletesMap) {
      for (const dateKey in teamSeries.dateTopAthletesMap) {
        if (!dateBasedKeyAllTeams[dateKey]) {
          dateBasedKeyAllTeams[dateKey] = new Set<string>()
        }
        dateBasedKeyAllTeams[dateKey].add(teamSeries.clubName)
      }
    }
  }

  //  need to sort dateBasedKeyAllTeams.
  const sortedDateBasedKeyAllTeams = Object.keys(dateBasedKeyAllTeams)
    .sort((a, b) => {
      const dateA = new Date(a.split('_')[0])
      const dateB = new Date(b.split('_')[0])
      return dateA.getTime() - dateB.getTime()
    })
    .reduce(
      (acc, key) => {
        acc[key] = dateBasedKeyAllTeams[key]
        return acc
      },
      {} as Record<string, Set<string>>
    )

  const columnsToLayout = Object.keys(dateBasedKeyAllTeams).length

  return {
    teamsForSeriesName,
    teamsByAthleteCount,
    maxAthletesPerTeam,
    series: qualifiedSeries,
    seriesNonQualified,
    dateBasedKeyAllTeams,
    sortedDateBasedKeyAllTeams,
    columnsToLayout
  }
}

// function that merges teams by club name across multiple egIds.
// needed for series that have multiple competition dates.
// recalculate positions and total scores accordingly.
// Make a map of club name to TeamResultSeries then return an array of TeamResultSeries
//
// New features:
// - egIdTopAthletesMap: Record<number, TeamAthleteDetail[]> - Groups top athletes by egId
//   Example: { 18201: [athlete1, athlete2], 18202: [athlete3, athlete4] }
//
// - positionTotals: Record<number, number> - Aggregates position scores across all egIds
//   Example: { 0: 8, 1: 12, 2: 15, 3: 20 } means:
//   - Total position 0 scores from all egIds: 8
//   - Total position 1 scores from all egIds: 12
//   - etc.
//
// Usage for position totals loop:
//   for (const series of mergedTeams) {
//     for (const [positionIndex, totalScore] of Object.entries(series.positionTotals || {})) {
//       console.log(`Position ${positionIndex}: Total score ${totalScore}`)
//     }
//   }
export function mergeTeamsByClub(
  teams: TeamResult[],
  howManyEgIdsRequired: number,
  maxAthletesPerTeam: number,
  storeCompResultsState: StoreCompResultsState
): ClubResultSeries[] {
  const teamSeriesMap: Record<string, ClubResultSeries> = {}

  for (const team of teams) {
    if (!teamSeriesMap[team.club]) {
      teamSeriesMap[team.club] = {
        clubName: team.club,
        egIds: [],
        teams: {},
        totalScore: 0,
        topAthletes: [],
        egIdTopAthletesMap: {},
        dateTopAthletesMap: {},
        qualification: {
          isQualified: false,
          details: ''
        }
      }
    }
    const teamSeries = teamSeriesMap[team.club]
    teamSeries.egIds.push(team.egId)
    teamSeries.teams[team.egId] = team

    // Initialize egIdTopAthletesMap for this egId if not exists
    if (!teamSeries.egIdTopAthletesMap) {
      teamSeries.egIdTopAthletesMap = {}
    }
    if (!teamSeries.egIdTopAthletesMap[team.egId]) {
      teamSeries.egIdTopAthletesMap[team.egId] = []
    }

    // Add top athletes for this egId, sorted by position
    const sortedAthletes = [...team.athletes]
      .sort((a, b) => a.position - b.position)
      .slice(0, maxAthletesPerTeam)

    teamSeries.egIdTopAthletesMap[team.egId].push(...sortedAthletes)

    // Initialize dateTopAthletesMap if not exists
    if (!teamSeries.dateTopAthletesMap) {
      teamSeries.dateTopAthletesMap = {}
    }

    // Find the schedule for this egId to get the eventDate
    const schedulesForEgId = storeCompResultsState.maps.eventsByEventGroupId[team.egId]
    if (schedulesForEgId && schedulesForEgId.length > 0) {
      // Create date-based key: eventDate_egId
      const dateBasedKey = `${schedulesForEgId[0].eventStartDate}_${team.egId}`

      // Initialize dateTopAthletesMap for this date-based key if not exist.
      if (!teamSeries.dateTopAthletesMap[dateBasedKey]) {
        teamSeries.dateTopAthletesMap[dateBasedKey] = 0
      }

      // add aggregated position score for this date-based key
      teamSeries.dateTopAthletesMap[dateBasedKey] += sortedAthletes.reduce(
        (sum, athlete) => sum + athlete.position,
        0
      )
    }

    // Keep the original topAthletes for backward compatibility
    teamSeries.topAthletes.push(...sortedAthletes)
  }

  // now sort topAthletes by position and recalculate totalScore based on top athletes only
  for (const club in teamSeriesMap) {
    const teamSeries = teamSeriesMap[club]

    // Sort and limit topAthletes for backward compatibility
    teamSeries.topAthletes = teamSeries.topAthletes
      .slice(0, maxAthletesPerTeam)
      .sort((a, b) => a.position - b.position)

    // Calculate totalScore from the top athletes
    teamSeries.totalScore = teamSeries.topAthletes
      .slice(0, maxAthletesPerTeam)
      .reduce((sum, athlete) => sum + athlete.position, 0)

    // Ensure egIdTopAthletesMap is properly initialized
    if (!teamSeries.egIdTopAthletesMap) {
      teamSeries.egIdTopAthletesMap = {}
    }
  }

  // Calculate position totals across egIds for easy looping
  for (const club in teamSeriesMap) {
    const teamSeries = teamSeriesMap[club]
    if (!teamSeries.egIdTopAthletesMap) continue

    // Create position totals by aggregating positions across all egIds
    const positionTotals: Record<number, number> = {}

    // For each position index (0, 1, 2, 3...) up to maxAthletesPerTeam
    for (let positionIndex = 0; positionIndex < maxAthletesPerTeam; positionIndex++) {
      let totalForThisPosition = 0
      const egIds = Object.keys(teamSeries.egIdTopAthletesMap)

      for (const egId of egIds) {
        const athletes = teamSeries.egIdTopAthletesMap[parseInt(egId)]
        if (athletes && athletes[positionIndex]) {
          totalForThisPosition += athletes[positionIndex].position
        }
      }

      positionTotals[positionIndex] = totalForThisPosition
    }

    // Add positionTotals to the teamSeries for easy access
    teamSeries.positionTotals = positionTotals
    teamSeries.totalScore = Object.values(positionTotals).reduce((sum, val) => sum + val, 0)
  }

  // now set qualification status
  for (const club in teamSeriesMap) {
    const teamSeries = teamSeriesMap[club]
    teamSeries.qualification.isQualified = teamSeries.egIds.length >= howManyEgIdsRequired
    teamSeries.qualification.details = teamSeries.qualification.isQualified
      ? 'Qualified'
      : `Needs ${howManyEgIdsRequired - teamSeries.egIds.length} more EGId ${maxAthletesPerTeam} athletes to qualify, needs ${howManyEgIdsRequired}`
  }

  const arrayOfSeries: ClubResultSeries[] = Object.values(teamSeriesMap)

  arrayOfSeries.sort((a, b) => a.totalScore - b.totalScore)

  return arrayOfSeries
}

// a function that takes a key like: 2025-11-01T00:00:00_18407 and returns 1st Nov.
export function formatDateBasedKey(dateBasedKey: string): string {
  const [dateStr, egIdStr] = dateBasedKey.split('_')
  const date = new Date(dateStr)
  const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }
  return date.toLocaleDateString(undefined, options)
}

// a function that takes a key like: 2025-11-01T00:00:00_18407 and returns the egId as number
export function getEgIdFromDateBasedKey(dateBasedKey: string): number {
  const [dateStr, egIdStr] = dateBasedKey.split('_')
  return parseInt(egIdStr)
}

// function that takes a ClubSeriesResult, an egId and a number and returns whether the athlete count for that egId is at least that number
export function isAthleteCountAtLeast(
  team: ClubResultSeries,
  dataKeyEg: string,
  count: number
): boolean {
  if (!team.egIdTopAthletesMap) {
    return false
  }
  const egId = getEgIdFromDateBasedKey(dataKeyEg)
  const athletes = team.egIdTopAthletesMap[egId]
  if (!athletes) {
    return false
  }
  return athletes ? athletes.length >= count : false
}

/**
 *
 * @param team THis function is only for applying css, so only interested if there are athletes.
 * If no athletes at all...then don't care
 * @param dataKeyEg
 * @param count
 * @returns
 */
export function isAthleteCountAtLeastCss(
  team: ClubResultSeries,
  dataKeyEg: string,
  count: number
): boolean {
  if (!team.egIdTopAthletesMap) {
    return true
  }
  const egId = getEgIdFromDateBasedKey(dataKeyEg)
  const athletes = team.egIdTopAthletesMap[egId]
  if (!athletes) {
    return true
  }
  return athletes ? athletes.length >= count : false
}
